import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';
import EmployerLayout from '../../layouts/EmployerLayout';
import Button from '../../components/common/Button';
import toast from 'react-hot-toast';

const STATUS_STYLE = {
  applied:     { cls: 'bg-blue-50 text-blue-700 border-blue-200',     label: 'Applied' },
  viewed:      { cls: 'bg-gray-50 text-gray-600 border-gray-200',     label: 'Viewed' },
  shortlisted: { cls: 'bg-yellow-50 text-yellow-700 border-yellow-200', label: 'Shortlisted' },
  hired:       { cls: 'bg-green-50 text-green-700 border-green-200',   label: 'Hired' },
  rejected:    { cls: 'bg-red-50 text-red-700 border-red-200',         label: 'Rejected' },
  withdrawn:   { cls: 'bg-gray-100 text-gray-500 border-gray-200',     label: 'Withdrawn' },
};

const NEXT_ACTIONS = {
  applied:     [['shortlisted', 'Shortlist'], ['rejected', 'Reject']],
  shortlisted: [['hired', 'Hire'], ['rejected', 'Reject']],
  hired:       [['shortlisted', 'Move back']],
};

export default function EmployerApplicants() {
  const { id } = useParams();
  const [reqTitle, setReqTitle] = useState('');
  const [apps, setApps] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [noteModal, setNoteModal] = useState(null);
  const [note, setNote] = useState('');
  const [updating, setUpdating] = useState(false);

  async function loadApps() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/requirements/${id}/applications?${params}`);
      setApps(res.data.data || []);
      setPagination(res.data.pagination || {});
    } finally { setLoading(false); }
  }

  useEffect(() => {
    api.get(`/requirements/${id}`).then((r) => setReqTitle(r.data.data?.title || '')).catch(() => {});
  }, [id]);

  useEffect(() => { loadApps(); }, [page, statusFilter]);

  async function handleStatusUpdate(applicationId, status) {
    setUpdating(applicationId);
    try {
      await api.patch(`/requirements/applications/${applicationId}/status`, { status, employer_note: note });
      toast.success('Status updated');
      setNoteModal(null);
      setNote('');
      loadApps();
    } finally { setUpdating(null); }
  }

  const counts = apps.reduce((acc, a) => ({ ...acc, [a.status]: (acc[a.status] || 0) + 1 }), {});

  return (
    <EmployerLayout>
      <div className="max-w-4xl mx-auto space-y-5">
        {/* Header */}
        <div>
          <Link to={`/employer/requirements/${id}`} className="text-sm text-gray-400 hover:text-gray-600 flex items-center gap-1 mb-2">
            ← Back to Requirement
          </Link>
          <h1 className="text-xl font-bold text-gray-900">Applicants</h1>
          {reqTitle && <p className="text-sm text-gray-500 mt-0.5">{reqTitle}</p>}
        </div>

        {/* Status summary chips */}
        <div className="flex gap-2 flex-wrap">
          {[['', 'All', pagination.total], ['applied','Applied', counts.applied], ['shortlisted','Shortlisted', counts.shortlisted], ['hired','Hired', counts.hired], ['rejected','Rejected', counts.rejected]].map(([val, label, count]) => (
            <button key={val} type="button"
              onClick={() => { setStatusFilter(val); setPage(1); }}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                statusFilter === val ? 'bg-india text-white border-india' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>
              {label} {count != null ? `(${count})` : ''}
            </button>
          ))}
        </div>

        {/* Applicant list */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-india border-t-transparent rounded-full" />
          </div>
        ) : apps.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <span className="text-4xl">👥</span>
            <p className="text-gray-500 mt-3">No applicants {statusFilter ? `with status "${statusFilter}"` : 'yet'}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {apps.map((app) => {
              const st = STATUS_STYLE[app.status] || STATUS_STYLE.applied;
              const actions = NEXT_ACTIONS[app.status] || [];
              return (
                <div key={app.id} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900 text-sm">{app.full_name}</h3>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${st.cls}`}>
                          {st.label}
                        </span>
                        {app.verification_status === 'verified' && (
                          <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">✓ Verified</span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-500">
                        {app.district && <span>📍 {app.district}</span>}
                        <span>💼 {app.experience_years} yrs exp</span>
                        {app.daily_wage_min && <span>💰 ₹{app.daily_wage_min}{app.daily_wage_max ? `–${app.daily_wage_max}` : ''}/day</span>}
                        <span>Applied: {new Date(app.applied_at).toLocaleDateString('en-IN')}</span>
                      </div>
                      {app.note && (
                        <div className="mt-2 bg-gray-50 rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-400 mb-0.5">Applicant note</p>
                          <p className="text-xs text-gray-700">{app.note}</p>
                        </div>
                      )}
                      {app.employer_note && (
                        <div className="mt-2 bg-blue-50 rounded-lg px-3 py-2">
                          <p className="text-xs text-blue-400 mb-0.5">Your note</p>
                          <p className="text-xs text-blue-800">{app.employer_note}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0 flex-wrap justify-end">
                      {actions.map(([nextStatus, label]) => (
                        <button key={nextStatus}
                          onClick={() => { setNoteModal({ id: app.id, status: nextStatus, name: app.full_name }); setNote(app.employer_note || ''); }}
                          disabled={updating === app.id}
                          className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${
                            nextStatus === 'hired'    ? 'bg-green-600 text-white border-green-600 hover:bg-green-700' :
                            nextStatus === 'rejected' ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' :
                            'bg-yellow-100 text-yellow-700 border-yellow-200 hover:bg-yellow-200'
                          }`}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-3">
            {pagination.hasPrev && <Button size="sm" variant="secondary" onClick={() => setPage(p => p - 1)}>← Prev</Button>}
            <span className="text-sm text-gray-500 py-1.5">Page {pagination.page} of {pagination.totalPages}</span>
            {pagination.hasNext && <Button size="sm" variant="secondary" onClick={() => setPage(p => p + 1)}>Next →</Button>}
          </div>
        )}
      </div>

      {/* Note Modal */}
      {noteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4">
            <h3 className="font-bold text-gray-900">
              {noteModal.status === 'hired' ? 'Hire' : noteModal.status === 'shortlisted' ? 'Shortlist' : 'Reject'} — {noteModal.name}
            </h3>
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Note for worker (optional)</label>
              <textarea value={note} onChange={(e) => setNote(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-20 focus:ring-2 focus:ring-brand-500 focus:outline-none resize-none"
                placeholder="Add a note for the worker..." />
            </div>
            <div className="flex gap-3">
              <Button variant="secondary" fullWidth onClick={() => { setNoteModal(null); setNote(''); }}>Cancel</Button>
              <Button
                fullWidth
                loading={updating === noteModal.id}
                variant={noteModal.status === 'rejected' ? 'danger' : 'primary'}
                onClick={() => handleStatusUpdate(noteModal.id, noteModal.status)}>
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}
    </EmployerLayout>
  );
}
